import React from 'react';
import { View } from 'react-native';
import { DashboardLayout } from 'app/components/web/dashboard-layout';
import { useIsGuestWeb, useUISize } from 'app/utils/web-helpers';
import { ExplorerLayout } from 'app/components/web/explorer-layout';
import { SimpleVerticalScrollingLayout } from 'app/components/web/simple-vertical-scrolling-layout';
import { ChatLayout } from 'app/components/web/chat-layout';
import { MessageFooterProvider } from 'app/screens/chat/message-footer-context';
interface WithDashboardLayoutProps {
  isDesktop: boolean;
}

export const DASHBOARD_LAYOUT_DEFAULT_LAYOUT = 'dashboard';
export const DASHBOARD_LAYOUT_DEFAULT_WITH_MESSAGE_FOOTER = false;

type LayoutName = 'dashboard' | 'explorer' | 'simpleVerticalScrolling' | 'chat';

export function withDashboardLayout<P extends object = object>(
  WrappedComponent: React.ComponentType<P & WithDashboardLayoutProps>,
  customLayout: LayoutName | ((isMobileUI: boolean) => LayoutName) = 'dashboard',
  withMessageFooter: boolean | ((isMobileUI: boolean) => boolean) = false,
  additionalLayoutProps?: Record<string, unknown>,
) {
  function ComponentWithDashboardLayout(props: P) {
    const { isSmallUI, isLargeUI } = useUISize();
    const isGuestWeb = useIsGuestWeb();
    const useDesktopLayout = isLargeUI || isGuestWeb;

    const resolvedLayout = typeof customLayout === 'function' ? customLayout(isSmallUI) : customLayout;
    const resolvedWithMessageFooter =
      typeof withMessageFooter === 'function' ? withMessageFooter(isSmallUI) : withMessageFooter;

    let Layout: React.ComponentType<{ children: React.ReactNode; withMessageFooter: boolean }>;

    switch (resolvedLayout) {
      case 'explorer':
        Layout = ExplorerLayout;
        break;
      case 'dashboard':
        Layout = DashboardLayout;
        break;
      case 'simpleVerticalScrolling':
        Layout = SimpleVerticalScrollingLayout;
        break;
      case 'chat':
        Layout = ChatLayout;
        break;
      default:
        Layout = DashboardLayout;
    }

    return useDesktopLayout ? (
      <MessageFooterProvider>
        <View style={{ flex: 1 }}>
          <Layout withMessageFooter={resolvedWithMessageFooter} {...(additionalLayoutProps as object)}>
            <WrappedComponent {...props} isDesktop={useDesktopLayout} />
          </Layout>
        </View>
      </MessageFooterProvider>
    ) : (
      <WrappedComponent {...props} isDesktop={useDesktopLayout} />
    );
  }

  ComponentWithDashboardLayout.displayName = `withDashboardLayout(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return ComponentWithDashboardLayout;
}
